import { useState, type ReactNode } from 'react'
import type { Person } from '@/api/pending'
import { fmtMonthYear } from '@/lib/format'
import { Avatar, Button, Caution, Eyebrow, Icon, Modal, Pill } from '@/ui'
import { useTaxIdCheck } from './api'
import { CpfGate, type CpfVerdict } from './CpfGate'
import { lastSeenLabel, S } from './strings'
import styles from './ReviewModal.module.css'

function Verified({ ok }: { ok: boolean }) {
  return (
    <span className={ok ? styles.verified : styles.unverified}>
      <Icon
        name={ok ? 'check' : 'flag'}
        size={14}
        color={ok ? 'var(--color-primary)' : undefined}
      />
      {ok ? S.verified : S.unverified}
    </span>
  )
}

function KV({
  label,
  value,
  verified,
  tabular,
}: {
  label: string
  value: ReactNode
  verified?: boolean
  tabular?: boolean
}) {
  return (
    <div className={styles.kv}>
      <Eyebrow>{label}</Eyebrow>
      <div className={styles.kvValue}>
        <span className={tabular ? styles.tabular : undefined}>{value}</span>
        {typeof verified === 'boolean' && <Verified ok={verified} />}
      </div>
    </div>
  )
}

interface ReviewModalProps {
  condoId: number
  person: Person
  /** Other accounts in the search results that share a first name or first surname. */
  lookAlikes: Person[]
  onCancel: () => void
  onContinue: () => void
}

/** Step 1: "É a pessoa certa?". Continuing needs the server to confirm the CPF's last four digits. */
export function ReviewModal({
  condoId,
  person,
  lookAlikes,
  onCancel,
  onContinue,
}: ReviewModalProps) {
  const [last4, setLast4] = useState('')
  const check = useTaxIdCheck(condoId, person.id, last4)
  const verdict: CpfVerdict =
    last4.length < 4
      ? 'idle'
      : check.isError
        ? 'error'
        : check.data === undefined
          ? 'checking'
          : check.data.match
            ? 'match'
            : 'mismatch'
  const name = `${person.first_name} ${person.last_name}`
  const hasUnverified = !person.email_verified || !person.phone_verified || !person.tax_id_verified

  return (
    <Modal
      width={560}
      title={S.step1Title}
      onClose={onCancel}
      footer={
        <>
          <Button variant="ghost" onClick={onCancel}>
            {S.cancel}
          </Button>
          <Button icon="check" disabled={verdict !== 'match'} onClick={onContinue}>
            {S.continue}
          </Button>
        </>
      }
    >
      <div className={styles.head}>
        <Avatar name={name} size={52} />
        <div className={styles.headText}>
          <div className={styles.name}>{name}</div>
          <div className={styles.account}>
            {S.account(person.id, fmtMonthYear(person.created_at))}
          </div>
        </div>
        <Pill tone={person.status === 'active' ? 'approved' : 'neutral'}>
          {person.status === 'active' ? S.active : S.inactive}
        </Pill>
      </div>
      <div className={styles.grid}>
        <KV label="E-mail" value={person.email} verified={person.email_verified} />
        <KV label="Telefone" value={person.phone} verified={person.phone_verified} tabular />
        <KV label="CPF" value={person.tax_id_masked} verified={person.tax_id_verified} tabular />
        <KV label="Último acesso" value={lastSeenLabel(person.last_seen)} />
        <KV
          label="Unidades neste condomínio"
          value={person.units.length ? person.units.join(', ') : 'Nenhuma ainda'}
        />
        <KV
          label="Outros condomínios"
          value={
            person.other_condos.length
              ? person.other_condos.map((c) => `${c.name} · ${c.unit}`).join(', ')
              : 'Nenhum'
          }
        />
      </div>
      {lookAlikes.length > 0 && <Caution icon="group">{S.cautionSimilar(lookAlikes)}</Caution>}
      {hasUnverified && <Caution icon="flag">{S.cautionUnverified}</Caution>}
      <CpfGate value={last4} onChange={setLast4} verdict={verdict} />
    </Modal>
  )
}
